/**
 * Pure helpers behind the Complete Import run controls: catalog progress across a
 * run chain, run-card bar segments, page-table formatting and filter counts.
 * Kept free of React/i18n so every number the screen shows is unit-testable.
 */

import type { PipelineRunEvent } from '~/features/integrations/types/pipeline.types';
import { describeRunFailure } from '~/features/integrations/utils/run-failure';
import { formatDurationMs, serverIsoToMillis } from '~/utils/date';

import type { CatalogImportView, WorkflowStatus } from '../types/catalog-import.types';
import type {
  IngestPageCounts,
  IngestPageFilter,
  IngestPageRow,
  IngestPageStatus,
  IngestRunLineage,
  IngestRunSummary,
} from '../types/ingest-run.types';

/** A page slower than this shows the "taking longer than usual" banner. */
export const SLOW_PAGE_THRESHOLD_MS = 30_000;
/** "Took" turns amber from here (normal pages take ~2–3s). */
export const PAGE_DURATION_WARN_MS = 5_000;

/** Run statuses that still change — lineage/pages keep polling while one is live. */
export function isLiveRunStatus(status: WorkflowStatus | null | undefined): boolean {
  return status === 'PENDING' || status === 'RUNNING' || status === 'PAUSED';
}

/** The run in the lineage that backs the current workflow, else the newest one. */
export function findRun(
  lineage: IngestRunLineage | undefined,
  runId: string | null | undefined,
): IngestRunSummary | undefined {
  if (!lineage) return undefined;
  return (
    lineage.runs.find((r) => r.runId === runId) ??
    lineage.runs.find((r) => r.runId === lineage.latestRunId) ??
    lineage.runs[lineage.runs.length - 1]
  );
}

/** Pages in the catalog: ceil(estimatedTotal / pageSize), `null` when unknown. */
export function estimatedPageCount(
  run: Pick<IngestRunSummary, 'estimatedTotal' | 'pageSize'> | undefined,
): number | null {
  if (!run?.estimatedTotal || run.pageSize <= 0) return null;
  return Math.ceil(run.estimatedTotal / run.pageSize);
}

/** Shown for an import that was never resumed (a first import or a resync). */
export const SINGLE_RUN_LABEL = 'Import';

/**
 * "Original run" only reads right once a retry follows it; a lone ORIGINAL run (a
 * first import or a resync) is just the import. Retry labels stay backend-owned.
 */
export function withDisplayLabels(lineage: IngestRunLineage): IngestRunLineage {
  const [only, ...rest] = lineage.runs;
  if (!only || rest.length > 0 || only.kind !== 'ORIGINAL') return lineage;
  return { ...lineage, runs: [{ ...only, label: SINGLE_RUN_LABEL }] };
}

/**
 * The runs whose imported pages a RESUME run keeps: its parent, the parent's parent,
 * … back to (and including) the nearest ORIGINAL / RESTART. Runs before a RESTART add
 * nothing — it re-imported from page 1. Empty for ORIGINAL / RESTART runs.
 */
export function keptFromRuns(
  lineage: IngestRunLineage | undefined,
  run: IngestRunSummary,
): IngestRunSummary[] {
  if (run.kind !== 'RESUME' || !lineage) return [];
  const byId = new Map(lineage.runs.map((r) => [r.runId, r]));
  const kept: IngestRunSummary[] = [];
  const seen = new Set<string>([run.runId]);
  let cur = run.parentRunId ? byId.get(run.parentRunId) : undefined;
  while (cur && !seen.has(cur.runId)) {
    seen.add(cur.runId);
    kept.push(cur);
    if (cur.kind !== 'RESUME') break;
    cur = cur.parentRunId ? byId.get(cur.parentRunId) : undefined;
  }
  return kept;
}

/** Products a RESUME run kept from earlier runs: what those runs actually imported
 *  (REMAINING resumes at the first page that was not imported, so their pages are
 *  contiguous and disjoint). 0 for ORIGINAL / RESTART. */
export function keptProducts(lineage: IngestRunLineage | undefined, run: IngestRunSummary): number {
  const kept = keptFromRuns(lineage, run).reduce((sum, r) => sum + r.productsImported, 0);
  return run.estimatedTotal === null ? kept : Math.min(kept, run.estimatedTotal);
}

/** The chain's newest run (`latestRunId`, else the last one listed). */
export function latestRun(lineage: IngestRunLineage | undefined): IngestRunSummary | undefined {
  if (!lineage) return undefined;
  return (
    lineage.runs.find((r) => r.runId === lineage.latestRunId) ??
    lineage.runs[lineage.runs.length - 1]
  );
}

/** The chain's latest run when it is a different run than `runId` and can be followed
 *  (has a workflow) — after a 409 NEWER_RUN_EXISTS / RUN_NOT_RESUMABLE the screen
 *  switches to it. */
export function newerRunToFollow(
  lineage: IngestRunLineage | undefined,
  runId: string | null | undefined,
): { runId: string; workflowId: string } | null {
  const latest = latestRun(lineage);
  if (!latest || latest.runId === runId || !latest.workflowId) return null;
  return { runId: latest.runId, workflowId: latest.workflowId };
}

export interface CatalogProgress {
  imported: number;
  total: number;
  percent: number;
}

/**
 * "X of Y products in your catalog". A resumed run's own counters cover only its
 * pages, so the products the earlier runs of its chain imported are added back;
 * without lineage (older backend, first poll) it falls back to the status progress.
 */
export function computeCatalogProgress(
  view: CatalogImportView,
  run: IngestRunSummary | undefined,
  lineage: IngestRunLineage | undefined,
): CatalogProgress {
  const total = run?.estimatedTotal ?? view.totalProducts;
  let imported = view.importedProducts;
  if (run) {
    imported =
      run.kind === 'RESUME'
        ? keptProducts(lineage, run) + run.productsImported
        : // The two polls tick independently — take whichever saw more.
          Math.max(run.productsImported, view.importedProducts);
  }
  if (total > 0) imported = Math.min(imported, total);
  const percent =
    view.phase === 'completed'
      ? 100
      : total > 0
        ? Math.floor((imported / total) * 100)
        : Math.floor(view.percentComplete);
  return { imported, total, percent };
}

/**
 * Seconds of ETA to show, or `null` to show none. Only a run that is actually
 * fetching has a meaningful ETA: never while paused (nothing moves), retrying a
 * page (the backoff is not throughput), stopped or finished. Shared by the
 * Complete Import card and the floating progress widget so they never disagree.
 */
export function visibleEtaSeconds(
  view: CatalogImportView,
  currentPage: IngestPageRow | null | undefined,
): number | null {
  if (view.phase !== 'running' || currentPage?.status === 'RETRYING') return null;
  return view.estimatedTimeRemainingSeconds;
}

/** The ORIGINAL / RESTART run a chain's current stretch started from (the run
 *  itself for ORIGINAL / RESTART) — its `expectedProducts` is the chain's scope. */
export function chainBaseRun(
  lineage: IngestRunLineage | undefined,
  run: IngestRunSummary,
): IngestRunSummary {
  return keptFromRuns(lineage, run).at(-1) ?? run;
}

/**
 * Working time of the import `run` finished: its own start→end plus that of every
 * earlier run whose products it kept (back to the chain's ORIGINAL/RESTART). The
 * idle gap before a resume is not counted. `null` when any run in that segment
 * lacks a start or end time (the caller keeps the status payload's figure).
 */
export function chainWorkSeconds(
  lineage: IngestRunLineage | undefined,
  run: IngestRunSummary | undefined,
): number | null {
  if (!run) return null;
  let total = 0;
  for (const r of [run, ...keptFromRuns(lineage, run)]) {
    const endIso = r.completedAt ?? r.failedAt ?? r.cancelledAt;
    if (!r.startedAt || !endIso) return null;
    const start = serverIsoToMillis(r.startedAt);
    const end = serverIsoToMillis(endIso);
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
    total += end - start;
  }
  return total / 1000;
}

/**
 * The completed card's stats, at catalog level like the running/stopped cards (a
 * resumed run adds back what earlier runs of its chain imported). The percentage
 * is the server's (the status mapper defaults it to 100 on COMPLETED) — client
 * math never shows a finished import as < 100%. A pull capped by `max_products`
 * reports against its cap (`expectedProducts` of the chain's base run), not the
 * whole catalog. Without the run (no lineage yet / older backend) it is the status
 * payload as-is.
 */
export function completedImportStats(
  view: CatalogImportView,
  run: IngestRunSummary | undefined,
  lineage: IngestRunLineage | undefined,
): { total: number; imported: number; percent: number } {
  const percent = Math.min(100, Math.max(0, view.percentComplete));
  if (!run) {
    return { total: view.totalProducts, imported: view.importedProducts, percent };
  }
  const progress = computeCatalogProgress(view, run, lineage);
  const base = chainBaseRun(lineage, run);
  const capped =
    base.expectedProducts !== null &&
    base.estimatedTotal !== null &&
    base.expectedProducts > 0 &&
    base.expectedProducts < base.estimatedTotal;
  const total = capped && base.expectedProducts !== null ? base.expectedProducts : progress.total;
  return {
    total,
    imported: total > 0 ? Math.min(progress.imported, total) : progress.imported,
    percent,
  };
}

/** Page the run stopped on: the status's `failedPage`, else the lineage's `stoppedPage`. */
export function stoppedAtPage(
  view: CatalogImportView,
  run: IngestRunSummary | undefined,
): number | null {
  return view.failedPage ?? run?.stoppedPage ?? null;
}

export interface ResumePoint {
  /** First page a REMAINING resume starts at (label only), `null` when unknown. */
  page: number | null;
  /** False when the server says there is no resume point (REMAINING is off). */
  available: boolean;
}

/**
 * Where a REMAINING resume would start. The server owns this: the latest run's
 * `resumeFromPage`. `null` means "no resume point" (REMAINING off) only once the
 * lineage itself shows that run FAILED/CANCELLED — the status poll and the lineage
 * poll tick independently, so a lineage still saying RUNNING while the status
 * already says failed is just behind (and null there means "not computed yet").
 * Until the lineage catches up — or while it loads, or on a backend that predates
 * the field — the status `failedPage` stands in as the label; if the lineage could
 * not be read at all the label stays generic and the POST decides (409
 * NO_RESUME_POINT).
 */
export function resolveResumePoint(
  view: CatalogImportView,
  lineage: IngestRunLineage | undefined,
  lineageLoading: boolean,
): ResumePoint {
  const latest = latestRun(lineage);
  if (latest?.resumeFromPage != null) return { page: latest.resumeFromPage, available: true };
  if (latest?.resumeFromPage === null && isTerminalStoppedStatus(latest.status)) {
    return { page: null, available: false };
  }
  if (latest || lineageLoading) return { page: view.failedPage ?? null, available: true };
  return { page: null, available: true };
}

/** A run that ended without finishing — the only kind a resume continues. */
export function isTerminalStoppedStatus(status: WorkflowStatus | null | undefined): boolean {
  return status === 'FAILED' || status === 'CANCELLED';
}

export interface RunSegments {
  expected: number;
  imported: number;
  active: number;
  stopped: number;
  /** Excludes the stopped page (that has its own segment). */
  notReached: number;
  /** Legend figure: everything this run did not import, incl. the stopped page;
   *  `null` while the run is live (it may still get there). */
  notReachedLabel: number | null;
}

/**
 * Stacked-bar segments for a run card, in products. A live run shows its current
 * page as "in progress"; a terminal run splits what it did not import into the
 * page it died on and the pages it never reached.
 */
export function runSegments(run: IngestRunSummary): RunSegments {
  const expected = run.expectedProducts ?? run.productsImported;
  const imported = Math.min(run.productsImported, expected || run.productsImported);
  const left = Math.max(0, expected - imported);
  if (isLiveRunStatus(run.status)) {
    return {
      expected,
      imported,
      active: Math.min(run.pageSize, left),
      stopped: 0,
      notReached: 0,
      notReachedLabel: null,
    };
  }
  const notReachedTotal = run.notReachedProducts ?? left;
  const stopped = run.stoppedPage !== null ? Math.min(run.pageSize, notReachedTotal) : 0;
  return {
    expected,
    imported,
    active: 0,
    stopped,
    notReached: Math.max(0, notReachedTotal - stopped),
    notReachedLabel: run.status === 'COMPLETED' ? 0 : notReachedTotal,
  };
}

/** A segment's width (%) of the run's expected products, one decimal. */
export function segmentWidth(value: number, expected: number): number {
  if (expected <= 0) return 0;
  return Math.round((value / expected) * 1000) / 10;
}

/** The filter pills the pages panel offers, in display order. "Pending" maps to the
 *  server's `ACTIVE` alias so the current page (in progress/retrying/paused) sits
 *  with the pages still to come. */
export const PAGE_FILTERS = ['ALL', 'IMPORTED', 'STOPPED', 'NOT_REACHED', 'ACTIVE'] as const;
export type PageFilterKey = (typeof PAGE_FILTERS)[number];

const ACTIVE_STATUSES: IngestPageStatus[] = ['IN_PROGRESS', 'RETRYING', 'PAUSED', 'PENDING'];

/** A filter pill's count from `countsByStatus` (which ignores the active filter). */
export function pageFilterCount(counts: IngestPageCounts | undefined, filter: IngestPageFilter) {
  if (!counts) return null;
  const read = (s: IngestPageStatus) => counts[s] ?? 0;
  if (filter === 'ALL') {
    return (Object.keys(counts) as IngestPageStatus[]).reduce((sum, s) => sum + read(s), 0);
  }
  if (filter === 'ACTIVE') return ACTIVE_STATUSES.reduce((sum, s) => sum + read(s), 0);
  return read(filter);
}

/** Page statuses backed by a real ledger row (the rest are virtual). */
const REAL_PAGE_STATUSES: IngestPageStatus[] = [
  'IMPORTED',
  'IN_PROGRESS',
  'RETRYING',
  'PAUSED',
  'STOPPED',
];

/**
 * True when the run has no usable page ledger — the worker ran with
 * `INGEST_PAGE_LEDGER_ENABLED` off (or before the ledger existed): products were
 * imported but no page was recorded as IMPORTED, or a finished run has only
 * virtual rows. The pages table would then list every page as "Pending" /
 * "Not reached", which is wrong, so the panel explains instead.
 */
export function isLedgerUnavailable(
  counts: IngestPageCounts | undefined,
  run: Pick<IngestRunSummary, 'productsImported' | 'status'>,
): boolean {
  if (!counts) return false;
  const read = (s: IngestPageStatus) => counts[s] ?? 0;
  if (read('IMPORTED') === 0 && run.productsImported > 0) return true;
  const real = REAL_PAGE_STATUSES.reduce((sum, s) => sum + read(s), 0);
  return real === 0 && !isLiveRunStatus(run.status);
}

/** A page row that exists in the ledger (not a virtual PENDING / NOT_REACHED row). */
export function isRealPageRow(row: Pick<IngestPageRow, 'status'>): boolean {
  return REAL_PAGE_STATUSES.includes(row.status);
}

/** "a–b of n" bounds for an offset page; zeros when empty. */
export function pagerBounds(offset: number, shown: number, total: number) {
  if (total <= 0 || shown <= 0) return { from: 0, to: 0, total: Math.max(0, total) };
  return { from: offset + 1, to: Math.min(total, offset + shown), total };
}

/** Page fetch time: one decimal under a minute ("2.1s"), else "2m 18s". */
export function formatPageDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return formatDurationMs(ms);
}

/** Tone of the "Took" cell: slow pages stand out without reading as errors. */
export function durationTone(ms: number | null | undefined): 'normal' | 'warn' | 'slow' {
  if (ms === null || ms === undefined) return 'normal';
  if (ms >= SLOW_PAGE_THRESHOLD_MS) return 'slow';
  if (ms >= PAGE_DURATION_WARN_MS) return 'warn';
  return 'normal';
}

/** Highest attempt number on a page (the one running or last tried). */
export function latestAttempt(row: Pick<IngestPageRow, 'attempts'>): number {
  return row.attempts.reduce((max, a) => Math.max(max, a.attempt), 0);
}

/** `code` of a page's highest-numbered attempt (the one that gave up on a STOPPED
 *  page); `null` when it has none — or on a backend that predates `code`. */
export function lastAttemptCode(row: Pick<IngestPageRow, 'attempts'>): string | null {
  let last: IngestPageRow['attempts'][number] | undefined;
  for (const a of row.attempts) if (!last || a.attempt > last.attempt) last = a;
  return last?.code ?? null;
}

/** True when the current page's running attempt has run past the slow threshold. */
export function isSlowPage(row: IngestPageRow, nowMs: number): boolean {
  if (row.status !== 'IN_PROGRESS') return false;
  const running = row.attempts.find((a) => a.outcome === 'RUNNING');
  if (!running?.startedAt) return false;
  const started = serverIsoToMillis(running.startedAt);
  return !Number.isNaN(started) && nowMs - started >= SLOW_PAGE_THRESHOLD_MS;
}

export interface FailureCopy {
  /** Plain-English line for the card subtitle. */
  message: string;
  /** Raw worker/Temporal text, for technical details only. */
  raw: string | null;
}

/**
 * The failed card's subtitle, most specific first: backend `userMessage` →
 * the app's own copy for the `errorCode` → `describeRunFailure` (shared with the
 * pipeline run page) → the generic fallback.
 */
export function resolveFailureCopy(
  input: {
    userMessage: string | null | undefined;
    errorCode: string | null | undefined;
    rawError: string | null | undefined;
  },
  copyForCode: (code: string) => string | null,
  fallback: string,
): FailureCopy {
  const raw = input.rawError?.trim() ? input.rawError.trim() : null;
  const user = input.userMessage?.trim();
  if (user) return { message: user, raw };
  const code = input.errorCode?.trim();
  const own = code ? copyForCode(code) : null;
  if (own) return { message: own, raw };
  const described = describeRunFailure(code, raw);
  if (described) {
    const message = described.detail
      ? `${described.title}: ${described.detail}`
      : `${described.title}.${described.hint ? ` ${described.hint}` : ''}`;
    return { message, raw };
  }
  return { message: fallback, raw };
}

/** One-line support reference the "Copy support reference" button puts on the clipboard. */
export function buildSupportReference(parts: {
  workflowId: string;
  pipelineRunId: string | null | undefined;
  stoppedPage: number | null;
  errorCode: string | null | undefined;
  /** `code` of the stopped page's last attempt (e.g. `RATE_LIMITED`), when known. */
  attemptCode?: string | null | undefined;
}): string {
  return [
    'Catalog import',
    `workflow ${parts.workflowId}`,
    parts.pipelineRunId ? `run ${parts.pipelineRunId}` : null,
    parts.stoppedPage !== null ? `stopped page ${String(parts.stoppedPage)}` : null,
    parts.errorCode ?? null,
    parts.attemptCode ? `last attempt ${parts.attemptCode}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export type EventTone = 'ok' | 'warn' | 'error' | 'info';

/** Dot colour for an activity event, read from its type and target status. */
export function eventTone(event: Pick<PipelineRunEvent, 'eventType' | 'toStatus'>): EventTone {
  const text = `${event.eventType} ${event.toStatus ?? ''}`.toUpperCase();
  if (/FAIL|ERROR|STOPPED/.test(text)) return 'error';
  if (text.includes('CANCEL')) return 'error';
  if (/PAUSE|RETRY|INTERRUPT|REJECT|SLOW|WARN/.test(text)) return 'warn';
  if (/COMPLETE|IMPORTED|SUCCE/.test(text)) return 'ok';
  return 'info';
}

/** Flatten "Load more" pages into one list in server order (`order=desc` → newest
 *  first), dropping an event repeated across a page boundary (new events shift the
 *  offsets of a live run between fetches, and the live head overlaps page 1). */
export function mergeEventPages(pages: readonly PipelineRunEvent[][]): PipelineRunEvent[] {
  const seen = new Set<string>();
  const out: PipelineRunEvent[] = [];
  for (const page of pages) {
    for (const e of page) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      out.push(e);
    }
  }
  return out;
}
